/**
 * NextJS context provider for cross-domain session management
 * Provides session state and actions through React context
 */

"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Session } from "@supabase/supabase-js";
import {
  createCrossDomainStorage,
  createCrossOriginMessaging,
} from "@/lib/utils/index";
import { getDomainInfo } from "@/lib/utils/domainDetection";
import { sessionUtils } from "@/lib/auth/sessionUtils";

export interface CrossDomainSessionContextType {
  // Session state
  session: Session | null;
  user: any | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Session actions
  setSession: (session: Session | null) => void;
  clearSession: () => void;
  refreshSession: () => Promise<void>;

  // Cross-domain functionality
  syncSession: () => Promise<void>;
  getSessionStatus: () => any;
  getDebugInfo: () => any;

  // Status information
  isConnected: boolean;
  lastSync: number | null;
  conflicts: string[];
  error: string | null;
}

const CrossDomainSessionContext =
  createContext<CrossDomainSessionContextType | null>(null);

export interface CrossDomainSessionProviderProps {
  children: React.ReactNode;
  enableDebug?: boolean;
  autoSync?: boolean;
  syncInterval?: number;
  onSessionChange?: (session: Session | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Cross-domain session provider component
 */
export function CrossDomainSessionProvider({
  children,
  enableDebug = process.env.NODE_ENV === "development",
  autoSync = true,
  syncInterval = 30000,
  onSessionChange,
  onError,
}: CrossDomainSessionProviderProps) {
  const [session, setSessionState] = useState<Session | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [conflicts, setConflicts] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const storageRef = useRef<ReturnType<typeof createCrossDomainStorage> | null>(
    null
  );
  const messagingRef = useRef<ReturnType<
    typeof createCrossOriginMessaging
  > | null>(null);
  const isInitializedRef = useRef(false);

  // Initialize cross-domain session management
  useEffect(() => {
    if (typeof window === "undefined") {
      setIsLoading(false);
      return;
    }

    const initializeSession = async () => {
      try {
        const domainInfo = getDomainInfo();

        // Only initialize in development or production environments
        if (!domainInfo.isDevelopment && !domainInfo.isProduction) {
          if (enableDebug) {
            console.log(
              "[CrossDomainSessionProvider] Skipping initialization - not in supported environment"
            );
          }
          setIsLoading(false);
          return;
        }

        // Initialize storage
        storageRef.current = createCrossDomainStorage({
          enableDebug,
          storageKey: "SUPABASE_SESSION",
          cookieDomain: domainInfo.cookieDomain,
          enablePostMessage: domainInfo.isDevelopment,
        });

        // Initialize messaging
        messagingRef.current = createCrossOriginMessaging({
          enableDebug,
          enableAutoSync: autoSync,
          syncInterval,
        });

        // Set up message handlers
        messagingRef.current.addMessageHandler(handleCrossDomainMessage);

        // Start listening
        messagingRef.current.startListening();

        // Load initial session
        await loadInitialSession();

        // Set up periodic sync
        if (autoSync) {
          const interval = setInterval(() => {
            syncSession();
          }, syncInterval);

          return () => clearInterval(interval);
        }

        isInitializedRef.current = true;
        setIsLoading(false);

        if (enableDebug) {
          console.log("[CrossDomainSessionProvider] Initialized successfully");
        }
      } catch (error) {
        console.error(
          "[CrossDomainSessionProvider] Initialization error:",
          error
        );
        setError(error instanceof Error ? error.message : "Unknown error");
        onError?.(error as Error);
        setIsLoading(false);
      }
    };

    initializeSession();

    return () => {
      cleanup();
    };
  }, [enableDebug, autoSync, syncInterval, onError]);

  // Load initial session from storage
  const loadInitialSession = useCallback(async () => {
    try {
      if (!storageRef.current) return;

      const sessionData = storageRef.current.getSessionData();

      if (sessionData && storageRef.current.isSessionValid()) {
        setSessionState(sessionData);
        setUser(sessionData.user);

        if (enableDebug) {
          console.log(
            "[CrossDomainSessionProvider] Loaded initial session for user:",
            sessionData.user?.id
          );
        }
      } else {
        // Clear invalid session
        storageRef.current.clearSession();
        setSessionState(null);
        setUser(null);
      }

      // Update status
      updateStatus();
    } catch (error) {
      console.error(
        "[CrossDomainSessionProvider] Error loading initial session:",
        error
      );
      setError(error instanceof Error ? error.message : "Unknown error");
    }
  }, [enableDebug]);

  // Handle cross-domain messages
  const handleCrossDomainMessage = useCallback(
    (message: any) => {
      if (enableDebug) {
        console.log("[CrossDomainSessionProvider] Received message:", message);
      }

      try {
        switch (message.action) {
          case "SESSION_LOGIN":
            handleSessionLogin(message);
            break;
          case "SESSION_LOGOUT":
            handleSessionLogout(message);
            break;
          case "SESSION_REFRESH":
            handleSessionRefresh(message);
            break;
          case "SESSION_SYNC":
            handleSessionSync(message);
            break;
        }
      } catch (error) {
        console.error(
          "[CrossDomainSessionProvider] Error handling message:",
          error
        );
        setError(error instanceof Error ? error.message : "Unknown error");
        onError?.(error as Error);
      }
    },
    [enableDebug, onError]
  );

  // Handle session login from other domains
  const handleSessionLogin = useCallback(
    (message: any) => {
      if (message.data) {
        try {
          const sessionData = JSON.parse(message.data);

          setSessionState(sessionData);
          setUser(sessionData.user);

          // Update storage
          if (storageRef.current) {
            storageRef.current.setSessionData(sessionData);
          }

          // Update status
          updateStatus();

          // Notify parent
          onSessionChange?.(sessionData);

          if (enableDebug) {
            console.log(
              "[CrossDomainSessionProvider] Session login synced from",
              message.source
            );
          }
        } catch (error) {
          console.error(
            "[CrossDomainSessionProvider] Error parsing session data:",
            error
          );
        }
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session logout from other domains
  const handleSessionLogout = useCallback(
    (message: any) => {
      setSessionState(null);
      setUser(null);

      // Clear storage
      if (storageRef.current) {
        storageRef.current.clearSession();
      }

      // Update status
      updateStatus();

      // Notify parent
      onSessionChange?.(null);

      if (enableDebug) {
        console.log(
          "[CrossDomainSessionProvider] Session logout synced from",
          message.source
        );
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session refresh from other domains
  const handleSessionRefresh = useCallback(
    (message: any) => {
      if (message.data) {
        try {
          const sessionData = JSON.parse(message.data);

          setSessionState(sessionData);
          setUser(sessionData.user);

          // Update storage
          if (storageRef.current) {
            storageRef.current.setSessionData(sessionData);
          }

          // Update status
          updateStatus();

          // Notify parent
          onSessionChange?.(sessionData);

          if (enableDebug) {
            console.log(
              "[CrossDomainSessionProvider] Session refresh synced from",
              message.source
            );
          }
        } catch (error) {
          console.error(
            "[CrossDomainSessionProvider] Error parsing session data:",
            error
          );
        }
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session sync request
  const handleSessionSync = useCallback(
    (message: any) => {
      // Send current session to requesting domain
      if (storageRef.current && messagingRef.current && session) {
        messagingRef.current.broadcastSessionUpdate(
          "SESSION_LOGIN",
          JSON.stringify(session)
        );
      }

      if (enableDebug) {
        console.log(
          "[CrossDomainSessionProvider] Session sync requested from",
          message.source
        );
      }
    },
    [session, enableDebug]
  );

  // Update status information
  const updateStatus = useCallback(() => {
    try {
      const sessionStatus = sessionUtils.getCrossDomainSessionStatus();
      const connectionStatus = messagingRef.current?.getConnectionStatus();

      setIsConnected(connectionStatus?.isListening || false);
      setLastSync(sessionStatus.lastSync);
      setConflicts(sessionStatus.conflicts);
    } catch (error) {
      console.error(
        "[CrossDomainSessionProvider] Error updating status:",
        error
      );
    }
  }, []);

  // Set session
  const setSession = useCallback(
    (newSession: Session | null) => {
      setSessionState(newSession);
      setUser(newSession?.user || null);

      if (storageRef.current) {
        if (newSession) {
          storageRef.current.setSessionData(newSession);
        } else {
          storageRef.current.clearSession();
        }
      }

      // Broadcast to other domains
      if (messagingRef.current) {
        if (newSession) {
          messagingRef.current.broadcastSessionUpdate(
            "SESSION_LOGIN",
            JSON.stringify(newSession)
          );
        } else {
          messagingRef.current.broadcastSessionUpdate("SESSION_LOGOUT", null);
        }
      }

      // Update status
      updateStatus();

      // Notify parent
      onSessionChange?.(newSession);
    },
    [onSessionChange, updateStatus]
  );

  // Clear session
  const clearSession = useCallback(() => {
    setSession(null);
  }, [setSession]);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      if (!session) return;

      // Update expiration time (simulate refresh)
      const refreshedSession = {
        ...session,
        expires_at: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
      };

      setSession(refreshedSession);

      // Broadcast refresh to other domains
      if (messagingRef.current) {
        messagingRef.current.broadcastSessionUpdate(
          "SESSION_REFRESH",
          JSON.stringify(refreshedSession)
        );
      }

      if (enableDebug) {
        console.log("[CrossDomainSessionProvider] Session refreshed");
      }
    } catch (error) {
      console.error(
        "[CrossDomainSessionProvider] Error refreshing session:",
        error
      );
      setError(error instanceof Error ? error.message : "Unknown error");
    }
  }, [session, enableDebug, setSession]);

  // Sync session across domains
  const syncSession = useCallback(async () => {
    try {
      await sessionUtils.syncSessionAcrossDomains();
      updateStatus();

      if (enableDebug) {
        console.log("[CrossDomainSessionProvider] Session synced");
      }
    } catch (error) {
      console.error(
        "[CrossDomainSessionProvider] Error syncing session:",
        error
      );
      setError(error instanceof Error ? error.message : "Unknown error");
    }
  }, [enableDebug, updateStatus]);

  // Get session status
  const getSessionStatus = useCallback(() => {
    return sessionUtils.getCrossDomainSessionStatus();
  }, []);

  // Get debug information
  const getDebugInfo = useCallback(() => {
    return sessionUtils.getDebugInfo();
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (messagingRef.current) {
      messagingRef.current.destroy();
      messagingRef.current = null;
    }

    isInitializedRef.current = false;

    if (enableDebug) {
      console.log("[CrossDomainSessionProvider] Cleaned up");
    }
  }, [enableDebug]);

  // Context value
  const contextValue: CrossDomainSessionContextType = {
    // Session state
    session,
    user,
    isLoading,
    isAuthenticated: !!session,

    // Session actions
    setSession,
    clearSession,
    refreshSession,

    // Cross-domain functionality
    syncSession,
    getSessionStatus,
    getDebugInfo,

    // Status information
    isConnected,
    lastSync,
    conflicts,
    error,
  };

  return (
    <CrossDomainSessionContext.Provider value={contextValue}>
      {children}
    </CrossDomainSessionContext.Provider>
  );
}

/**
 * Hook to use cross-domain session context
 */
export function useCrossDomainSession(): CrossDomainSessionContextType {
  const context = useContext(CrossDomainSessionContext);

  if (!context) {
    throw new Error(
      "useCrossDomainSession must be used within a CrossDomainSessionProvider"
    );
  }

  return context;
}

/**
 * Hook for session state only
 */
export function useSession() {
  const { session, user, isLoading, isAuthenticated } = useCrossDomainSession();

  return {
    session,
    user,
    isLoading,
    isAuthenticated,
  };
}

/**
 * Hook for session actions only
 */
export function useSessionActions() {
  const { setSession, clearSession, refreshSession, syncSession } =
    useCrossDomainSession();

  return {
    setSession,
    clearSession,
    refreshSession,
    syncSession,
  };
}

/**
 * Hook for cross-domain status
 */
export function useCrossDomainStatus() {
  const {
    isConnected,
    lastSync,
    conflicts,
    error,
    getSessionStatus,
    getDebugInfo,
  } = useCrossDomainSession();

  return {
    isConnected,
    lastSync,
    conflicts,
    error,
    getSessionStatus,
    getDebugInfo,
  };
}

export default CrossDomainSessionProvider;
